import { useState, type FormEvent } from 'react';
import { api, type CurrentUser } from '../api';
import { t } from '../i18n';

export function Login({ onLogin }: { onLogin: (u: CurrentUser) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'password' | 'code'>('password');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submitPassword = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.login(email, password);
      if ('twoFactorRequired' in r) setStep('code');
      else onLogin(r);
    } catch {
      setError(t('auth.login.error'));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onLogin(await api.verify2fa(code));
    } catch {
      setError(t('auth.2fa.error'));
    } finally {
      setBusy(false);
    }
  };

  if (step === 'code') {
    return (
      <form className="form narrow card" onSubmit={submitCode}>
        <h1>{t('auth.2fa.title')}</h1>
        <p className="muted">{t('auth.2fa.hint')}</p>
        <label>
          {t('auth.2fa.code')}
          <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
        </label>
        {error && <div className="error" role="alert">{error}</div>}
        <button className="btn" type="submit" disabled={busy}>{t('auth.2fa.submit')}</button>
      </form>
    );
  }

  return (
    <form className="form narrow card" onSubmit={submitPassword}>
      <h1>{t('auth.login.title')}</h1>
      <label>
        {t('auth.login.email')}
        <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </label>
      <label>
        {t('auth.login.password')}
        <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
      </label>
      {error && <div className="error" role="alert">{error}</div>}
      <button className="btn" type="submit" disabled={busy}>{t('auth.login.submit')}</button>
    </form>
  );
}
